import { BaseSettingsItem, BaseSettingsItemProps, Chip, Icon, Select } from '@elipssolution/harfang';
import { mdiCheck, mdiClose } from '@mdi/js';
import { styled } from '@mui/material';
import { useMemo } from 'react';

const PermissionChip = styled(Chip)({
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-start',
});

enum PermissionValueEnum {
	AUTHORIZED = 'authorized',
	REJECTED = 'rejected',
}

type PermissionOptionType = {
	label: string;
	value: PermissionValueEnum;
};

const permissionOptions: PermissionOptionType[] = [
	{ value: PermissionValueEnum.AUTHORIZED, label: 'Autorisé' },
	{ value: PermissionValueEnum.REJECTED, label: 'Refusé' },
];

const PermissionOptionChip = ({
	label,
	value,
	withIcon,
}: PermissionOptionType & {
	withIcon?: boolean;
}) => {
	const chipColor = useMemo(() => (value === PermissionValueEnum.AUTHORIZED ? 'success' : 'error'), [value]);

	const chipIcon = useMemo(
		() => <Icon path={value === PermissionValueEnum.AUTHORIZED ? mdiCheck : mdiClose} />,
		[value],
	);

	return withIcon ? (
		<PermissionChip color={chipColor} icon={chipIcon} label={label} />
	) : (
		<PermissionChip color={chipColor} label={label} />
	);
};

type SettingsItemPermissionProps = BaseSettingsItemProps & {
	/** If `true`, the `Select` is disabled. */
	disabled?: boolean;
	/** The value herited from the group. */
	inheritedValue?: boolean;
	/** If `true`, the component shows a loading indicator. */
	loading?: boolean;
	/** Callback triggered when a new value is selected. */
	onChange: (value?: boolean) => void;
	/** The value of the `Select`. */
	value?: boolean;
};

const SettingsItemPermission = ({
	disabled,
	description,
	icon,
	inheritedValue,
	label,
	loading,
	onChange,
	value,
	...rest
}: SettingsItemPermissionProps) => {
	const hasInheritance = inheritedValue !== undefined;
	const isInherited = hasInheritance && (value === undefined || value === null);

	const currentValue = useMemo(() => {
		if (isInherited) {
			return permissionOptions.find(
				({ value: permissionValue }) =>
					permissionValue === (inheritedValue ? PermissionValueEnum.AUTHORIZED : PermissionValueEnum.REJECTED),
			);
		}

		return permissionOptions.find(
			({ value: permissionValue }) =>
				permissionValue === (value ? PermissionValueEnum.AUTHORIZED : PermissionValueEnum.REJECTED),
		);
	}, [inheritedValue, isInherited, value]);

	const handleMenuItemClick = ({ option: { value: optionValue } }: { option: PermissionOptionType }) =>
		onChange(optionValue === PermissionValueEnum.AUTHORIZED);

	const handleResetClick = () => onChange(undefined);

	return (
		<BaseSettingsItem
			description={description}
			hasInheritance={hasInheritance}
			icon={icon}
			isInherited={isInherited}
			label={label}
			actionWidth={`${hasInheritance ? 180 : 180 - 40 - 8}px !important`}
			onInheritanceReset={handleResetClick}
		>
			<Select<PermissionOptionType>
				{...rest}
				disabled={disabled}
				// Not `onChange` used because it doesn't trigger on same value selection
				// Which is needed for the inheritance gestion
				onMenuItemClick={handleMenuItemClick}
				options={permissionOptions}
				placeholder={currentValue ? undefined : 'Non défini'}
				renderOption={(option) => <PermissionOptionChip {...option} withIcon />}
				renderValue={(option) => <PermissionOptionChip {...option} />}
				value={currentValue}
				disableClearable
				sx={{ flex: 1 }}
			/>
		</BaseSettingsItem>
	);
};

export default SettingsItemPermission;
